/**
 * "Your materials" (bug fix 1, spec 5 §3): the textbook, the past papers and the drop zone.
 * Once everything in it is settled it folds to one line — the counts, "Add files" and
 * "Show". It opens on its own whenever something in it needs the student or is moving, and
 * then stays open for the visit, so it never jumps shut under them. The student's own Show
 * or Hide wins for the rest of the visit.
 */
import { useEffect, useState, type ReactNode } from "react";

type Props = {
  /** Still loading: shown open (its skeleton), and it decides nothing about folding. */
  loading?: boolean;
  attention: boolean;
  summary: string;
  /** "Add files" while folded: open, and bring up the file picker. */
  onAddFiles: () => void;
  /** Kept on the folded line: what the materials are for (Study, once a book is ready). */
  primary?: ReactNode;
  children: ReactNode;
};

export function MaterialsSection({
  loading = false,
  attention,
  summary,
  onAddFiles,
  primary,
  children,
}: Props) {
  const [stayOpen, setStayOpen] = useState(attention);
  const [choice, setChoice] = useState<boolean | null>(null);
  useEffect(() => {
    if (attention) setStayOpen(true);
  }, [attention]);
  const open = loading || (choice ?? (stayOpen || attention));
  return (
    <section aria-labelledby="materials" className="materials">
      <div className="materials__head">
        <h2 id="materials" className="eyebrow subject__eyebrow">
          Your materials
        </h2>
        {open && !attention && !loading && (
          <button
            type="button"
            className="button button--quiet materials__toggle"
            aria-expanded={true}
            onClick={() => setChoice(false)}
          >
            Hide
          </button>
        )}
      </div>
      {open ? (
        children
      ) : (
        <div className="materials__summary">
          <span className="materials__counts">{summary}</span>
          <span className="materials__actions">
            {primary}
            <button
              type="button"
              className="button"
              onClick={() => {
                setChoice(true);
                onAddFiles();
              }}
            >
              Add files
            </button>
            <button
              type="button"
              className="button button--quiet"
              aria-expanded={false}
              onClick={() => setChoice(true)}
            >
              Show
            </button>
          </span>
        </div>
      )}
    </section>
  );
}
